/** 프로필 사진 크롭. 화면에서 고른 정사각만 96으로 남긴다. 512·비공개 저장은 아직 없다. */

export const AVATAR_VIEW = 240;
export const AVATAR_ZOOM_MIN = 1;
export const AVATAR_ZOOM_MAX = 3;
export const AVATAR_MAX_EDGE = 8000;
export const AVATAR_MAX_BYTES = 10 * 1024 * 1024;

export type CropFrame = {
  width: number;
  height: number;
  zoom: number;
  x: number;
  y: number;
};

export function rejectAvatarFile(file: { type: string; name: string; size: number }): string | null {
  if (file.type === "image/gif" || file.type === "image/svg+xml" || /\.(gif|svg)$/i.test(file.name)) {
    return "GIF와 SVG는 올리지 않습니다";
  }
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return "JPEG, PNG, WebP만 올릴 수 있습니다";
  }
  if (file.size > AVATAR_MAX_BYTES) return "10MB보다 작은 사진만 올릴 수 있습니다";
  return null;
}

export function rejectAvatarEdge(width: number, height: number): string | null {
  if (width < 1 || height < 1) return "이 브라우저에서는 사진을 열 수 없습니다";
  if (Math.max(width, height) > AVATAR_MAX_EDGE) return "긴 변이 8,000px보다 작은 사진만 올릴 수 있습니다";
  return null;
}

export function coverScale(width: number, height: number, view = AVATAR_VIEW): number {
  return Math.max(view / width, view / height);
}

export function initialCrop(width: number, height: number, view = AVATAR_VIEW): CropFrame {
  const scale = coverScale(width, height, view);
  return clampCrop({
    width,
    height,
    zoom: 1,
    x: (view - width * scale) / 2,
    y: (view - height * scale) / 2,
  }, view);
}

export function displayedSize(frame: CropFrame, view = AVATAR_VIEW) {
  const scale = coverScale(frame.width, frame.height, view) * frame.zoom;
  return { width: frame.width * scale, height: frame.height * scale, scale };
}

export function clampCrop(frame: CropFrame, view = AVATAR_VIEW): CropFrame {
  const shown = displayedSize(frame, view);
  return {
    ...frame,
    zoom: Math.min(AVATAR_ZOOM_MAX, Math.max(AVATAR_ZOOM_MIN, frame.zoom)),
    x: Math.min(0, Math.max(view - shown.width, frame.x)),
    y: Math.min(0, Math.max(view - shown.height, frame.y)),
  };
}

export function moveCrop(frame: CropFrame, dx: number, dy: number, view = AVATAR_VIEW): CropFrame {
  return clampCrop({ ...frame, x: frame.x + dx, y: frame.y + dy }, view);
}

function roundZoom(zoom: number): number {
  return Math.round(zoom * 100) / 100;
}

/** 보이는 칸의 가운데가 같은 픽셀을 가리키게 확대한 뒤, 빈 칸이 없게 붙인다. */
export function zoomCrop(frame: CropFrame, zoom: number, view = AVATAR_VIEW): CropFrame {
  const next = roundZoom(Math.min(AVATAR_ZOOM_MAX, Math.max(AVATAR_ZOOM_MIN, zoom)));
  if (next === frame.zoom) return frame;
  const ratio = next / frame.zoom;
  const x = view / 2 - (view / 2 - frame.x) * ratio;
  const y = view / 2 - (view / 2 - frame.y) * ratio;
  return clampCrop({ ...frame, zoom: next, x, y }, view);
}

export function panStep(shiftKey: boolean): number {
  return shiftKey ? 10 : 1;
}

export function panByKey(frame: CropFrame, key: string, shiftKey: boolean, view = AVATAR_VIEW): CropFrame | null {
  const step = panStep(shiftKey);
  if (key === "ArrowLeft") return moveCrop(frame, -step, 0, view);
  if (key === "ArrowRight") return moveCrop(frame, step, 0, view);
  if (key === "ArrowUp") return moveCrop(frame, 0, -step, view);
  if (key === "ArrowDown") return moveCrop(frame, 0, step, view);
  return null;
}

export function zoomByKey(frame: CropFrame, key: string, view = AVATAR_VIEW): CropFrame | null {
  if (key === "+" || key === "=") return zoomCrop(frame, frame.zoom + 0.1, view);
  if (key === "-" || key === "_") return zoomCrop(frame, frame.zoom - 0.1, view);
  return null;
}

/** 뷰포트에 보이는 정사각을 원본 좌표로 바꾼다. */
export function sourceRect(frame: CropFrame, view = AVATAR_VIEW) {
  const { scale } = displayedSize(frame, view);
  return {
    x: -frame.x / scale,
    y: -frame.y / scale,
    size: view / scale,
  };
}

export function drawAvatar(image: CanvasImageSource, frame: CropFrame, size = 96): string {
  const rect = sourceRect(frame);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("이 브라우저에서는 사진을 줄일 수 없습니다");
  ctx.drawImage(image, rect.x, rect.y, rect.size, rect.size, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.85);
}
